import React, {useEffect, useState} from "react";
import {defaultLayouts, GSForm, GSItemList, GSSidePanelPage,} from "golfstatus_react_components";
import {faChevronLeft} from "@fortawesome/free-solid-svg-icons";
import {useMatch, useNavigate} from "react-router-dom";

//import scss
import "./tournament-round-remove-hole-assignments.scss";
import {getRemoveTitle, removeItemForm} from "../../../../forms/DeleteForm";
import {useDispatch, useSelector} from "react-redux";
import {
  clearRoundNotifications,
  getRound,
  getRoundHoleAssignmentList,
  getTournamentRounds,
  removeHoleAssignments,
  selectCurrentRound,
  selectCurrentRoundHoleAssignments,
  selectLoading as selectRoundLoading,
  selectRoundNotifications,
} from "../../../../reducers/roundSlice";
import {getTournamentTeams, selectLoading,} from "../../../../reducers/teamsSlice";
import { getTournamentFlights, selectFlightLoading } from "../../../../reducers/flightSlice";
import { getName } from "../../../../app/api";
import {useNotificationBanner, useNotificationNavigation} from "../../../../hooks/notificationHooks";
import TournamentRoundHoleAssignmentListItem from "./tournament-round-hole-assignment-list-item";
import { useRoundHooks, getRoundLabel } from "../../../../hooks/round/roundHooks";
import { groupShotgunHoleAssignments, groupTeeTimeHoleAssignments } from "../../../../hooks/round/roundHoleAssignmentHooks";

//Name the component
const TournamentRoundRemoveHoleAssignments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const match = useMatch(
    "/:tournamentID/rounds/:roundID/hole-assignments/remove-all"
  );

  // selectors
  const currentRound = useSelector(selectCurrentRound);
  //a round's own label stands on its own. without one it is named for its number
  //and the sub round its siblings make it, which they are needed to work out.
  const { allRounds } = useRoundHooks();
  const roundLabel = getRoundLabel(currentRound, allRounds);
  const currentRoundHoleAssignments = useSelector(selectCurrentRoundHoleAssignments);
  const notifications = useSelector(selectRoundNotifications);
  const teamLoading = useSelector(selectLoading);
  const roundLoading = useSelector(selectRoundLoading);
  const flightLoading = useSelector(selectFlightLoading);

  //the round and its siblings name the page, its assignments and the teams fill
  //the cards, and the flights label them, so the page waits on all of them
  const pageRequests = [
    getRound,
    getTournamentRounds,
    getRoundHoleAssignmentList,
    getTournamentTeams,
    getTournamentFlights,
  ].map(getName);
  const pendingRequests = [...teamLoading, ...roundLoading, ...flightLoading];

  //until this round's data has been asked for, what's in the store belongs to
  //whatever page was open before
  const [requestedRoundID, setRequestedRoundID] = useState(null);
  const loading =
    requestedRoundID !== match?.params?.roundID ||
    pageRequests.some((request) => pendingRequests.includes(request));

  //custom hooks
  useNotificationNavigation("removed", notifications, -2);

  //effects
  useEffect(() => {
    if (match?.params?.roundID) {
      dispatch(getRound(match.params.roundID));
      dispatch(getRoundHoleAssignmentList(match.params.roundID));
      setRequestedRoundID(match.params.roundID);
    }
  }, [dispatch, match?.params?.roundID]);

  useEffect(() => {
    if (match?.params?.tournamentID) {
      dispatch(getTournamentTeams(match.params.tournamentID));
      //the cards name each team's flight, which comes from the tournament's flights
      dispatch(getTournamentFlights(match.params.tournamentID));
    }
    return () => {dispatch(clearRoundNotifications())}
  }, [dispatch, match?.params?.tournamentID]);

  //component functionality
  const removeAllHoleAssignments = () => {
    dispatch(removeHoleAssignments(match?.params?.roundID));
  };

  const getDrawerActions = () => {
    let actions = [
      {
        name: "Clear",
        action: removeAllHoleAssignments,
        type: "red",
      },
      { name: "cancel", action: leftNavigation, type: "grey" },
    ];
    return actions;
  };

  const leftNavigation = () => {
    navigate(-1);
  };

  const getNavigation = () => {
    //Add Title to your component
    return {
      title: `Clear ${roundLabel} Hole Assignments`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  //only the hole assignments holding teams are shown
  const getAssignedHoleAssignments = () => {
    return currentRoundHoleAssignments?.filter?.(
      (ha) => ha.roundHoleAssignmentTournamentTeams?.length > 0,
    );
  };

  const getHoleAssignments = () => {
    if (currentRound?.startType === "shotgun_start") {
      return groupShotgunHoleAssignments(getAssignedHoleAssignments());
    } else {
      return groupTeeTimeHoleAssignments(getAssignedHoleAssignments());
    }
  };

  const getHoleAssignment = (assignments) => {
    return (
      <TournamentRoundHoleAssignmentListItem
        key={assignments?.[0]?.id}
        holeAssignments={assignments}
        readOnly={true}
        assignedOnly={true}
      />
    );
  };

  const getHoleAssignmentList = () => {
    return (
      <GSItemList
        style={{...defaultLayouts.fill}}
        type={"vertical"}
        items={getHoleAssignments()}
        listItem={(item) => getHoleAssignment(item)}
      ></GSItemList>
    );
  };

  const getContent = () => {
    return (
      <GSForm
        formTitle={getRemoveTitle(`Clear ${roundLabel} Hole Assignments`)}
        formSections={removeItemForm(
          `${roundLabel.toLowerCase()} hole assignments`,
          getHoleAssignmentList(),
          "clear",
        )}
      ></GSForm>
    );
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  const timeoutAction = () => {
    dispatch(clearRoundNotifications())
  }

  const [banner] = useNotificationBanner({notifications, timeoutAction})

  return (
    //name the component tag
    <tournament-round-remove-hole-assignments>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        content={getContent()}
        drawer={getDrawer()}
        loading={loading}
        loadingMainText={"Loading Hole Assignments..."}
      ></GSSidePanelPage>
    </tournament-round-remove-hole-assignments>
  );
};

//export the new name
export default TournamentRoundRemoveHoleAssignments;
